import { DESTINATIONS, getDestinationHref } from "@/config/destinations";
import { DESTINATION_ROUTES, getPortugalPath } from "@/config/destinationRoutes";
import type { SiteLanguage } from "@/config/platform";

export type DestinationNavItem = {
  id: string;
  flag: string;
  nameKey: string;
  descriptionKey: string;
  href: string;
  highlighted?: boolean;
};

/** Destination hub links — canonical /destinations/* paths */
export function getDestinationNavItems(lang: SiteLanguage): DestinationNavItem[] {
  return DESTINATIONS.map((d) => ({
    id: d.id,
    flag: d.flag,
    nameKey: `destinations.${d.id}.name`,
    descriptionKey: `destinations.${d.id}.tagline`,
    href: getDestinationHref(d, lang),
    highlighted: d.isFlagship,
  }));
}

/** Portugal service pages — canonical paths under /destinations/portugal */
export function getPortugalServiceNavItems(lang: SiteLanguage) {
  return [
    { key: "nav.realEstate", route: getPortugalPath("realEstate", lang) },
    { key: "nav.buyProperty", route: getPortugalPath("buyProperty", lang) },
    { key: "nav.business", route: getPortugalPath("business", lang) },
    { key: "healthcare", route: getPortugalPath("healthcare", lang) },
    { key: "nav.expatGuides", route: getPortugalPath("guides", lang) },
  ];
}

/** Quick links shown at the bottom of the Destinations mega-menu */
export function getDestinationsMegaMenuQuickLinks(lang: SiteLanguage) {
  return [
    { key: "nav.pathways", route: "/pathways" },
    { key: "nav.toolkit", route: "/interactive" },
  ] as const;
}

export function getPortugalVisaNavItems(lang: SiteLanguage) {
  const p = DESTINATION_ROUTES.portugal;
  const isEn = lang === "en";
  return [
    {
      key: isEn ? "All Visas" : "Todos os Vistos",
      route: getPortugalPath("visas.all", lang),
      description: isEn ? "Complete Visa Guide" : "Guia Completo de Vistos",
      highlighted: true,
    },
    {
      key: isEn ? "Golden Visa" : "Visto Gold",
      route: getPortugalPath("visas.golden", lang),
      description: isEn ? "Investment Immigration" : "Imigração por Investimento",
    },
    {
      key: "Startup Visa",
      route: getPortugalPath("visas.startup", lang),
      description: isEn ? "Residence for Entrepreneurs" : "Residência para Empreendedores",
    },
    {
      key: isEn ? "D1 Visa" : "Visto D1",
      route: getPortugalPath("visas.d1", lang),
      description: isEn ? "Subordinate Work" : "Trabalho Subordinado",
    },
    {
      key: isEn ? "D2 Visa" : "Visto D2",
      route: getPortugalPath("visas.d2", lang),
      description: isEn ? "Entrepreneur / Self-Employed" : "Empreendedor / Trabalhador Independente",
    },
    {
      key: isEn ? "D3 Visa" : "Visto D3",
      route: getPortugalPath("visas.d3", lang),
      description: isEn ? "Research / Science / Training" : "Investigação / Ciência / Formação",
    },
    {
      key: isEn ? "D4 Visa" : "Visto D4",
      route: getPortugalPath("visas.d4", lang),
      description: isEn ? "Student" : "Estudante",
    },
    {
      key: isEn ? "D5 Visa" : "Visto D5",
      route: getPortugalPath("visas.d5", lang),
      description: isEn ? "Student Mobility" : "Mobilidade Estudantil",
    },
    {
      key: isEn ? "D6 Visa" : "Visto D6",
      route: getPortugalPath("visas.d6", lang),
      description: isEn ? "Family Reunification" : "Reagrupamento Familiar",
    },
    {
      key: isEn ? "D7 Visa" : "Visto D7",
      route: getPortugalPath("visas.d7", lang),
      description: isEn ? "Retirees / Own Income" : "Aposentados / Rendimentos Próprios",
    },
    {
      key: isEn ? "D8 Visa" : "Visto D8",
      route: getPortugalPath("visas.d8", lang),
      description: isEn ? "Digital Nomads" : "Nômades Digitais",
    },
  ];
}
